/**
 * In-memory cache for sync errors to avoid refetching the same data repeatedly.
 *
 * Sync errors are fetched when displaying the sync badge component.
 *
 * Cache key format: "orgCode:connectionId:jobId"
 * Cache persists for the lifetime of the application session.
 */

const syncErrorsCache = new Map();

/**
 * Generates a cache key for sync errors.
 *
 * @param {string} organizationCode - Organization code
 * @param {string|number} connectionId - Connection ID
 * @param {string|number} jobId - Job execution ID
 * @returns {string} Cache key
 */
export function getCacheKey(organizationCode, connectionId, jobId) {
  return `${organizationCode || ""}:${connectionId || ""}:${jobId || ""}`;
}

/**
 * Retrieves cached sync errors.
 *
 * @param {string} cacheKey - Cache key
 * @returns {Array|undefined} Cached errors or undefined if not cached
 */
export function getCachedSyncErrors(cacheKey) {
  return syncErrorsCache.get(cacheKey);
}

/**
 * Stores sync errors in the cache.
 *
 * @param {string} cacheKey - Cache key
 * @param {Array} errors - Array of sync error objects
 */
export function setCachedSyncErrors(cacheKey, errors) {
  syncErrorsCache.set(cacheKey, errors);
}

/**
 * Clears all cached sync errors.
 * Useful for testing or when cache invalidation is needed.
 */
export function clearSyncErrorsCache() {
  syncErrorsCache.clear();
}
